import { ExpandMoreRounded } from "@mui/icons-material";
import {
    Box,
    Chip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
    KboAnalysisDimensionResult,
    KboCohortDefinition,
    KboCohortDimension,
    KboMultiLevelAnalysisResult,
    KboMultiLevelFinding,
} from "../KboFlowDebug/KboFlowRunDebug";
import { formatNumber, shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { Stat, StepNumber } from "./KboFlowRunDetailPrimitives";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import { formatPercent } from "./KboFlowRunDigestModels";

/** The step-by-step "how KBo narrowed it down" walkthrough of one analysis. */

// `KboMultiLevelAnalysisResult.maxPasses` carries the real `maxDepth` a run
// executed with. This is only a fallback for runs persisted before that
// field existed — KboFlow's MultiLevelAnalysisService default is 2.
const FALLBACK_MAX_ANALYSIS_PASSES = 2;

function cohortFilterExpression(cohort?: KboCohortDefinition): string {
    return (
        cohort?.filters
            ?.map(
                (filter) =>
                    `${filter.alias} ${filter.operator} "${String(
                        filter.value ?? "",
                    ).trim()}"`,
            )
            .join(" AND ") ?? "No filters recorded"
    );
}

/** True when a dimension split a cohort into groups that are nearly all the same size as the cohort itself — i.e. it didn't meaningfully separate the population. */
function partitionBarelySplitCohort(
    results: KboAnalysisDimensionResult[],
    cohortCount?: number,
): { flagged: boolean; nearFullGroups: number; minCount: number } {
    const counts = results
        .map((result) => result.distribution?.count ?? 0)
        .filter((count) => count > 0);
    if (!cohortCount || counts.length < 2) {
        return { flagged: false, nearFullGroups: 0, minCount: 0 };
    }
    const nearFullGroups = counts.filter(
        (count) => count / cohortCount > 0.9,
    ).length;
    return {
        flagged: nearFullGroups >= counts.length - 1,
        nearFullGroups,
        minCount: Math.min(...counts),
    };
}

/** `OutlineSegmentKey` → `Outline Segment Key`, leaving acronyms intact. */
function humanizeAlias(alias?: string): string {
    if (!alias) return "—";
    return alias
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .trim();
}

function joinWithAnd(values: string[]): string {
    if (values.length === 0) return "";
    if (values.length === 1) return values[0];
    if (values.length === 2) return `${values[0]} and ${values[1]}`;
    return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
}

function dimensionValueLabel(result: KboAnalysisDimensionResult): string {
    const value = result.distribution?.value;
    return value === undefined || value === null || value === ""
        ? "Unlabelled"
        : String(value);
}

/**
 * Reports the group sizes the way the numbers actually fell — values that came
 * back at the same count are described together, e.g. "Clarification, Closing,
 * and Greeting all showed 1,518, and Verification showed 1,513".
 */
function groupSizePhrase(results: KboAnalysisDimensionResult[]): string {
    const byCount = new Map<number, string[]>();
    results.forEach((result) => {
        const count = result.distribution?.count ?? 0;
        byCount.set(count, [
            ...(byCount.get(count) ?? []),
            dimensionValueLabel(result),
        ]);
    });
    const segments = Array.from(byCount.entries())
        .sort((a, b) => b[0] - a[0])
        .map(
            ([count, values]) =>
                `${joinWithAnd(values)} ${
                    values.length > 1 ? "all showed" : "showed"
                } ${formatNumber(count)}`,
        );
    return joinWithAnd(segments);
}

/**
 * A collapsed detail row. `plain` sits under a dashed rule as an uppercase
 * caption; `bordered` is a full-width outlined row.
 */
function Disclosure({
    label,
    variant = "bordered",
    children,
}: {
    label: string;
    variant?: "plain" | "bordered";
    children: React.ReactNode;
}) {
    const [expanded, setExpanded] = useState(false);
    const plain = variant === "plain";
    return (
        <Box
            sx={
                plain
                    ? {
                          borderColor: "divider",
                          borderTop: "1px dashed",
                          mt: 1.5,
                          pt: 1.25,
                      }
                    : {
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                          mt: 1.25,
                          px: 1.5,
                          py: 1.25,
                      }
            }
        >
            <Box
                component="button"
                type="button"
                onClick={() => setExpanded((value) => !value)}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    color: plain ? pageColors.muted : pageColors.ink,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    fontSize: plain ? 11 : 13,
                    fontWeight: plain ? 800 : 700,
                    gap: 0.5,
                    letterSpacing: plain ? "0.06em" : undefined,
                    p: 0,
                    textAlign: "left",
                    textTransform: plain ? "uppercase" : "none",
                    width: "100%",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        fontSize: plain ? 16 : 18,
                        transform: expanded ? "none" : "rotate(-90deg)",
                        transition: "transform 120ms",
                    }}
                />
                {label}
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box sx={{ mt: 1.25 }}>{children}</Box>
            </Collapse>
        </Box>
    );
}

/** One finding: its claim, the reasoning behind it, and its evidence base. */
function FindingBlock({
    finding,
    prominent,
}: {
    finding: KboMultiLevelFinding;
    prominent?: boolean;
}) {
    const repCount = finding.representativeConversationIds?.length ?? 0;
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: prominent ? undefined : 14,
                    fontWeight: prominent ? 800 : 700,
                    lineHeight: 1.5,
                }}
            >
                {finding.claim ?? "No claim recorded."}
            </Typography>
            {finding.reasoning && (
                <Typography
                    variant="body2"
                    sx={{ color: pageColors.muted, lineHeight: 1.6, mt: 0.75 }}
                >
                    {finding.reasoning}
                </Typography>
            )}
            {repCount > 0 && !prominent && (
                <Chip
                    size="small"
                    variant="outlined"
                    sx={{ color: pageColors.muted, mt: 0.75 }}
                    label={`${formatNumber(repCount)} representative conversation${
                        repCount === 1 ? "" : "s"
                    }`}
                />
            )}
        </Box>
    );
}

/** Every dimension the cohort offered, with the one KBo picked marked. */
function DimensionChips({
    dimensions,
    chosenAlias,
}: {
    dimensions: KboCohortDimension[];
    chosenAlias?: string;
}) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {dimensions.map((dimension, index) => {
                const chosen =
                    !!chosenAlias && dimension.alias === chosenAlias;
                return (
                    <Box
                        component="span"
                        key={dimension.alias ?? index}
                        sx={{
                            bgcolor: chosen
                                ? pageColors.blueSoft
                                : pageColors.white,
                            border: "1px solid",
                            borderColor: chosen ? pageColors.blue : "divider",
                            borderRadius: 1,
                            color: chosen ? pageColors.blue : pageColors.muted,
                            fontSize: 12,
                            fontWeight: chosen ? 800 : 600,
                            px: 1,
                            py: 0.5,
                        }}
                    >
                        {humanizeAlias(dimension.alias)}
                    </Box>
                );
            })}
        </Box>
    );
}

/** Every group the dimension produced, with its size, share, and finding count. */
function GroupSizeTable({
    results,
    cohortCount,
}: {
    results: KboAnalysisDimensionResult[];
    cohortCount?: number;
}) {
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        Conversations
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        Share of cohort
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        Summaries read
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        Findings
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {results.map((result, index) => {
                    const count = result.distribution?.count;
                    const share =
                        result.distribution?.share ??
                        (cohortCount && count ? count / cohortCount : undefined);
                    const reviewed =
                        result.reviewedMaterialCount ??
                        result.analysisMaterial?.length;
                    return (
                        <TableRow key={index}>
                            <TableCell>{dimensionValueLabel(result)}</TableCell>
                            <TableCell align="right">
                                {formatNumber(count)}
                            </TableCell>
                            <TableCell align="right">
                                {typeof share === "number"
                                    ? formatPercent(share)
                                    : "—"}
                            </TableCell>
                            <TableCell align="right">
                                {formatNumber(reviewed)}
                            </TableCell>
                            <TableCell align="right">
                                {result.findings?.length ?? 0}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

function AnalysisNarrativeStep({
    number,
    title,
    meta,
    children,
}: {
    number: number;
    title: string;
    meta?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{ borderColor: "divider", borderRadius: 1.5, p: 1.75 }}
        >
            <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                <StepNumber number={number} color={pageColors.ink} />
                <Typography sx={{ flex: 1, fontWeight: 750 }}>
                    {title}
                </Typography>
                {meta}
            </Box>
            <Box sx={{ mt: 1.25 }}>{children}</Box>
        </Paper>
    );
}

/** A light blue pill, matching the step counts in the mock. */
function StepMeta({ label }: { label: string }) {
    return (
        <Chip
            size="small"
            label={label}
            sx={{
                bgcolor: pageColors.blueSoft,
                color: pageColors.blue,
                fontWeight: 700,
            }}
        />
    );
}

export function AnalysisNarrative({
    analysis,
    totalConversations,
}: {
    analysis: KboMultiLevelAnalysisResult;
    totalConversations?: number;
}) {
    const passes = analysis.passes ?? [];
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ color: pageColors.muted, lineHeight: 1.6 }}>
                KBo fixes the population it will study, picks one way to cut it
                into groups, reads the calls in each group, and decides whether
                it has enough to stop. The sub-steps below show what was
                recorded at each stage.
            </Typography>
            {passes.map((pass, passIndex) => {
                const cohort =
                    passIndex === 0
                        ? pass.analyzedCohort ?? analysis.initialCohort
                        : pass.analyzedCohort;
                const cohortCount =
                    passIndex === 0
                        ? pass.analyzedCohortCount ??
                          analysis.initialCohortCount
                        : pass.analyzedCohortCount;
                const results = pass.results ?? [];
                const selectedId = pass.selection?.findingId;
                const selectedResult = results.find((result) =>
                    result.findings?.some(
                        (finding) => finding.findingId === selectedId,
                    ),
                );
                const selectedFinding = selectedResult?.findings?.find(
                    (finding) => finding.findingId === selectedId,
                );
                const otherFindings = (selectedResult?.findings ?? []).filter(
                    (finding) => finding.findingId !== selectedId,
                );
                const selectedGroup = selectedResult
                    ? dimensionValueLabel(selectedResult)
                    : undefined;
                const selectedReviewed =
                    selectedResult?.reviewedMaterialCount ??
                    selectedResult?.analysisMaterial?.length;
                const selectedRepCount =
                    selectedFinding?.representativeConversationIds?.length ?? 0;
                const candidateFindings = results.reduce(
                    (total, result) => total + (result.findings?.length ?? 0),
                    0,
                );
                const filterCount = cohort?.filters?.length ?? 0;
                const offeredDimensions = cohort?.dimensions ?? [];
                const otherDimensionNames = offeredDimensions
                    .filter(
                        (dimension) =>
                            dimension.alias !== pass.dimension?.alias,
                    )
                    .map((dimension) => humanizeAlias(dimension.alias));
                const reviewedCounts = results.map(
                    (result) =>
                        result.reviewedMaterialCount ??
                        result.analysisMaterial?.length ??
                        0,
                );
                const perGroupMax = Math.max(...reviewedCounts, 0);
                const totalReviewed = reviewedCounts.reduce(
                    (total, count) => total + count,
                    0,
                );
                const perGroupCoverages = results
                    .map((result, index) => {
                        const groupSize = result.distribution?.count;
                        return groupSize
                            ? reviewedCounts[index] / groupSize
                            : undefined;
                    })
                    .filter((value): value is number => value !== undefined);
                const avgCoverage =
                    perGroupCoverages.length > 0
                        ? perGroupCoverages.reduce((a, b) => a + b, 0) /
                          perGroupCoverages.length
                        : undefined;
                const partition = partitionBarelySplitCohort(
                    results,
                    cohortCount,
                );

                return (
                    <Box
                        key={pass.passId ?? passIndex}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        {passes.length > 1 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: pageColors.muted,
                                    fontWeight: 800,
                                    textTransform: "uppercase",
                                }}
                            >
                                Pass {passIndex + 1} of {passes.length}
                            </Typography>
                        )}

                        <AnalysisNarrativeStep
                            number={1}
                            title="Cohort formation"
                            meta={
                                <StepMeta
                                    label={`${formatNumber(
                                        cohortCount,
                                    )} conversations`}
                                />
                            }
                        >
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                }}
                            >
                                The cohort KBo settled on for this analysis is
                                every conversation matching{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: pageColors.ink,
                                        fontWeight: 700,
                                    }}
                                >
                                    {cohortFilterExpression(cohort)}
                                </Box>{" "}
                                — {formatNumber(cohortCount)} in total, out of
                                the {formatNumber(totalConversations)}{" "}
                                conversations in the window.{" "}
                                {filterCount === 1
                                    ? "That single filter is the only one that actually executed, so nothing was excluded beyond it."
                                    : filterCount > 1
                                      ? `Those ${filterCount} filters were applied together.`
                                      : "No filters were applied — this cohort covers the full conversation window."}
                            </Typography>
                            {cohort?.name && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: pageColors.muted,
                                        lineHeight: 1.6,
                                        mt: 1,
                                    }}
                                >
                                    KBo named the cohort{" "}
                                    <Box
                                        component="span"
                                        sx={{ color: pageColors.ink }}
                                    >
                                        “{cohort.name}”
                                    </Box>
                                    {cohort.role ? ` (${cohort.role})` : ""}.
                                    {cohort.rationale
                                        ? ` ${cohort.rationale}`
                                        : ""}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    bgcolor: pageColors.blueSoft,
                                    borderRadius: 1,
                                    color: pageColors.blue,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    mt: 1.25,
                                    overflowX: "auto",
                                    px: 1.25,
                                    py: 1,
                                }}
                            >
                                {cohortFilterExpression(cohort)}
                            </Box>
                        </AnalysisNarrativeStep>

                        <AnalysisNarrativeStep
                            number={2}
                            title="Dimension selection"
                        >
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                }}
                            >
                                Having fixed the cohort, KBo has to choose one
                                way to cut it into comparable groups.
                                {offeredDimensions.length > 1
                                    ? ` It was offered ${offeredDimensions.length} dimensions and had to pick exactly one for this pass.`
                                    : ""}{" "}
                                It chose{" "}
                                <Box
                                    component="span"
                                    sx={{
                                        color: pageColors.ink,
                                        fontWeight: 700,
                                    }}
                                >
                                    {pass.dimension?.alias ?? "no dimension"}
                                </Box>
                                . In this cohort that dimension takes{" "}
                                {results.length} value
                                {results.length === 1 ? "" : "s"}, so the{" "}
                                {formatNumber(cohortCount)} conversations were
                                split into {results.length} group
                                {results.length === 1 ? "" : "s"} —{" "}
                                {joinWithAnd(results.map(dimensionValueLabel))}.
                            </Typography>
                            {offeredDimensions.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: pageColors.muted,
                                            fontWeight: 800,
                                            letterSpacing: "0.06em",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Dimensions offered
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 1.5,
                                            mt: 0.75,
                                            p: 1.25,
                                        }}
                                    >
                                        <DimensionChips
                                            dimensions={offeredDimensions}
                                            chosenAlias={pass.dimension?.alias}
                                        />
                                    </Paper>
                                </Box>
                            )}
                            {(pass.dimension?.reason ||
                                offeredDimensions.some(
                                    (dimension) => dimension.reason,
                                )) && (
                                <Disclosure
                                    variant="plain"
                                    label={
                                        otherDimensionNames.length > 0
                                            ? `Why this one over the other ${otherDimensionNames.length}`
                                            : "Why this dimension"
                                    }
                                >
                                    {pass.dimension?.reason && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: pageColors.muted,
                                                fontStyle: "italic",
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {pass.dimension.reason}
                                        </Typography>
                                    )}
                                    {offeredDimensions
                                        .filter(
                                            (dimension) =>
                                                dimension.reason &&
                                                dimension.alias !==
                                                    pass.dimension?.alias,
                                        )
                                        .map((dimension, index) => (
                                            <Typography
                                                key={dimension.alias ?? index}
                                                variant="body2"
                                                sx={{
                                                    color: pageColors.muted,
                                                    lineHeight: 1.6,
                                                    mt: 1,
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: pageColors.ink,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {humanizeAlias(
                                                        dimension.alias,
                                                    )}
                                                    :
                                                </Box>{" "}
                                                {dimension.reason}
                                            </Typography>
                                        ))}
                                </Disclosure>
                            )}
                        </AnalysisNarrativeStep>

                        <AnalysisNarrativeStep
                            number={3}
                            title="Measuring each group"
                            meta={
                                <StepMeta
                                    label={`${formatNumber(
                                        cohortCount,
                                    )} conversations`}
                                />
                            }
                        >
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                }}
                            >
                                {partition.flagged
                                    ? "Every group came back at essentially the full cohort size — "
                                    : ""}
                                {groupSizePhrase(results)} — out of{" "}
                                {formatNumber(cohortCount)} in the cohort.
                            </Typography>
                            {results.length > 0 && (
                                <Disclosure
                                    label={`Group sizes for all ${results.length} value${
                                        results.length === 1 ? "" : "s"
                                    }`}
                                >
                                    <GroupSizeTable
                                        results={results}
                                        cohortCount={cohortCount}
                                    />
                                </Disclosure>
                            )}
                        </AnalysisNarrativeStep>

                        <AnalysisNarrativeStep
                            number={4}
                            title="Reading the calls"
                            meta={
                                <StepMeta
                                    label={`${formatNumber(
                                        totalReviewed,
                                    )} summaries`}
                                />
                            }
                        >
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                    mb: 1.25,
                                }}
                            >
                                KBo read up to {formatNumber(perGroupMax)}{" "}
                                summaries per group rather than every
                                conversation in it,{" "}
                                {formatNumber(totalReviewed)} in total across
                                the {results.length} group
                                {results.length === 1 ? "" : "s"}.
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gap: 1,
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(160px, 1fr))",
                                }}
                            >
                                <Stat
                                    value={formatNumber(perGroupMax)}
                                    label="summaries per group"
                                />
                                <Stat
                                    value={formatNumber(totalReviewed)}
                                    label="summaries read in total"
                                />
                                <Stat
                                    value={
                                        avgCoverage !== undefined
                                            ? `~${formatPercent(
                                                  avgCoverage,
                                                  0,
                                              )}`
                                            : "—"
                                    }
                                    label="coverage per group"
                                />
                            </Box>
                        </AnalysisNarrativeStep>

                        <AnalysisNarrativeStep
                            number={5}
                            title="What came back"
                            meta={
                                selectedRepCount > 0 ? (
                                    <StepMeta
                                        label={`${formatNumber(
                                            selectedRepCount,
                                        )} conversations`}
                                    />
                                ) : undefined
                            }
                        >
                            {selectedFinding ? (
                                <>
                                    <Typography
                                        sx={{
                                            color: pageColors.muted,
                                            lineHeight: 1.6,
                                            mb: 1.25,
                                        }}
                                    >
                                        KBo selected the finding from the{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                color: pageColors.ink,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {selectedGroup ?? "selected"}
                                        </Box>{" "}
                                        group, out of{" "}
                                        {formatNumber(candidateFindings)}{" "}
                                        candidate finding
                                        {candidateFindings === 1 ? "" : "s"}{" "}
                                        raised across all {results.length} group
                                        {results.length === 1 ? "" : "s"}.
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            bgcolor: pageColors.blueSoft,
                                            borderColor: pageColors.blue,
                                            borderRadius: 1.5,
                                            p: 1.75,
                                        }}
                                    >
                                        <FindingBlock
                                            finding={selectedFinding}
                                            prominent
                                        />
                                    </Paper>
                                    {otherFindings.length > 0 && (
                                        <Disclosure
                                            label={`${
                                                otherFindings.length
                                            } other finding${
                                                otherFindings.length === 1
                                                    ? ""
                                                    : "s"
                                            } from the ${
                                                selectedGroup ?? "selected"
                                            } group`}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 1.5,
                                                }}
                                            >
                                                {otherFindings.map(
                                                    (finding, index) => (
                                                        <FindingBlock
                                                            key={
                                                                finding.findingId ??
                                                                index
                                                            }
                                                            finding={finding}
                                                        />
                                                    ),
                                                )}
                                            </Box>
                                        </Disclosure>
                                    )}
                                    <Disclosure
                                        variant="plain"
                                        label="How this was determined"
                                    >
                                        <Box
                                            component="dl"
                                            sx={{
                                                display: "grid",
                                                gap: 0.5,
                                                gridTemplateColumns:
                                                    "auto 1fr",
                                                m: 0,
                                            }}
                                        >
                                            {[
                                                [
                                                    "Finding id",
                                                    shortId(
                                                        selectedFinding.findingId,
                                                    ),
                                                ],
                                                [
                                                    "Group",
                                                    selectedGroup ?? "—",
                                                ],
                                                [
                                                    "Summaries read in that group",
                                                    formatNumber(
                                                        selectedReviewed,
                                                    ),
                                                ],
                                                [
                                                    "Representative conversations",
                                                    formatNumber(
                                                        selectedRepCount,
                                                    ),
                                                ],
                                                [
                                                    "Candidate findings compared",
                                                    formatNumber(
                                                        candidateFindings,
                                                    ),
                                                ],
                                            ].map(([term, value]) => (
                                                <React.Fragment key={term}>
                                                    <Box
                                                        component="dt"
                                                        sx={{
                                                            color: pageColors.muted,
                                                            fontSize: 13,
                                                            pr: 2,
                                                        }}
                                                    >
                                                        {term}
                                                    </Box>
                                                    <Box
                                                        component="dd"
                                                        sx={{
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            m: 0,
                                                        }}
                                                    >
                                                        {value}
                                                    </Box>
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </Disclosure>
                                </>
                            ) : (
                                <Typography color="text.secondary">
                                    No finding was selected for this pass.
                                </Typography>
                            )}
                        </AnalysisNarrativeStep>

                        <AnalysisNarrativeStep number={6} title="Stop decision">
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    lineHeight: 1.6,
                                }}
                            >
                                {pass.selection?.shouldContinue === false
                                    ? `Stopped after ${
                                          passIndex + 1
                                      } pass${passIndex === 0 ? "" : "es"}.`
                                    : "Continued to another pass."}
                                {pass.selection?.reason
                                    ? ` ${pass.selection.reason}`
                                    : ""}
                            </Typography>
                            <Chip
                                size="small"
                                label={`${passIndex + 1} of ${
                                    analysis.maxPasses ??
                                    FALLBACK_MAX_ANALYSIS_PASSES
                                } permitted passes used`}
                                sx={{ mt: 1 }}
                            />
                        </AnalysisNarrativeStep>

                        {partition.flagged && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    bgcolor: pageColors.orangeSoft,
                                    borderColor: "#FED7AA",
                                    p: 1.75,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: pageColors.orange,
                                        fontWeight: 800,
                                    }}
                                >
                                    Worth flagging
                                </Typography>
                                <Typography
                                    sx={{
                                        color: pageColors.muted,
                                        lineHeight: 1.6,
                                        mt: 0.5,
                                    }}
                                >
                                    The {pass.dimension?.alias ?? "chosen"}{" "}
                                    dimension split this cohort into{" "}
                                    {results.length} groups, but{" "}
                                    {partition.nearFullGroups} of them each
                                    contain nearly the entire cohort — even the
                                    smallest holds{" "}
                                    {formatNumber(partition.minCount)} of{" "}
                                    {formatNumber(cohortCount)} conversations,
                                    so at most{" "}
                                    {formatNumber(
                                        Math.max(
                                            (cohortCount ?? 0) -
                                                partition.minCount,
                                            0,
                                        ),
                                    )}{" "}
                                    were left out of any one group. This
                                    dimension didn't meaningfully separate the
                                    population. The selected finding may still
                                    be valid, but the slicing itself doesn't
                                    demonstrate real segmentation.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
